import type { ReactNode } from 'react';
import { CameraIcon, ClockIcon, ForwardIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import { DmButton, TOOLTIP_OVER_MODAL, type HeroIcon } from './DmControlPrimitives';
import type { MonitoringState } from './dmControlUtils';
import { patientRequestDisabledReason } from './patientRequests';

const PENDING_TIP =
    'A forced aligner change is pending. The patient will be told to switch to their next aligner.';

const ICON_TONE = {
    teal: 'bg-[#007E87]/10 text-[#007E87]',
    yellow: 'bg-yellow-100 text-yellow-700'
} as const;

// Only the icon carries the colour, and it greys out with a disabled button.
function TintedIcon({ icon: Icon, tone }: { icon: HeroIcon; tone: keyof typeof ICON_TONE }) {
    return (
        <span
            className={`-ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${ICON_TONE[tone]} group-disabled:bg-gray-100 group-disabled:text-gray-400`}
        >
            <Icon className="h-3.5 w-3.5" />
        </span>
    );
}

interface WithTooltipProps {
    tip: string;
    /** Only for a disabled button, which gets no hover or focus: the wrapper becomes the tab stop. */
    focusable?: boolean;
    children: ReactNode;
}

// DmButton takes no ref, so a span is the trigger; focus on an enabled button bubbles up to it.
function WithTooltip({ tip, focusable = false, children }: WithTooltipProps) {
    return (
        <Tooltip content={tip} className={TOOLTIP_OVER_MODAL} focusable={focusable}>
            <span className="flex w-full">{children}</span>
        </Tooltip>
    );
}

interface RequestButtonProps {
    icon: HeroIcon;
    label: string;
    disabledReason: string | null;
    onClick: () => void;
}

function RequestButton({ icon, label, disabledReason, onClick }: RequestButtonProps) {
    const button = (
        <DmButton className="group w-full" disabled={disabledReason !== null} onClick={onClick}>
            <TintedIcon icon={icon} tone="teal" />
            {label}
        </DmButton>
    );
    return disabledReason ? (
        <WithTooltip tip={disabledReason} focusable>
            {button}
        </WithTooltip>
    ) : (
        button
    );
}

interface PatientRequestButtonsProps {
    status: MonitoringState;
    alignerChangePending: boolean;
    onRequestScan: () => void;
    /** Absent for fixed appliances, which have no aligner to force or cancel. */
    onAlignerChange?: () => void;
}

// Asks DM for something from the patient now; cancelling a pending change ignores the status.
export function PatientRequestButtons({
    status,
    alignerChangePending,
    onRequestScan,
    onAlignerChange
}: PatientRequestButtonsProps) {
    const disabledReason = patientRequestDisabledReason(status);
    return (
        <>
            <RequestButton
                icon={CameraIcon}
                label="Request additional scan"
                disabledReason={disabledReason}
                onClick={onRequestScan}
            />
            {onAlignerChange &&
                (alignerChangePending ? (
                    <WithTooltip tip={PENDING_TIP}>
                        <DmButton
                            kind="warningOutline"
                            className="group w-full"
                            onClick={onAlignerChange}
                        >
                            <TintedIcon icon={ClockIcon} tone="yellow" />
                            Cancel forced change
                        </DmButton>
                    </WithTooltip>
                ) : (
                    <RequestButton
                        icon={ForwardIcon}
                        label="Force next aligner change"
                        disabledReason={disabledReason}
                        onClick={onAlignerChange}
                    />
                ))}
        </>
    );
}
